import React, { useState } from "react";
import { FaGlobeAsia, FaBrain, FaBolt, FaLayerGroup, FaDownload, FaUpload } from "react-icons/fa";

interface HowToUseProps {
  onNavigateToGenerator: () => void;
}

export const HowToUse: React.FC<HowToUseProps> = ({ onNavigateToGenerator }) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  return (
    <div className="generator" style={{ maxWidth: "1000px", margin: "0 auto", paddingBottom: "60px" }}>
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <h1 style={{ color: "#e11d48", fontSize: "2.5rem", marginBottom: "15px" }}>
          How to Use ScoreSmart
        </h1>
        <p style={{ fontSize: "1.2rem", color: "#64748b" }}>
          Master all the tools to supercharge your UPSC preparation
        </p>
      </div>

      {/* TOOL SECTIONS */}
      <div style={{ display: "grid", gap: "20px", marginBottom: "50px" }}>
        {/* FLASHCARD GENERATOR */}
        <ToolSection
          icon={<FaBolt size={24} color="#8b5cf6" />}
          title="Flashcard Generator"
          description="Transform any text into smart flashcards using AI"
          isActive={activeSection === "flashcards"}
          onToggle={() => setActiveSection(activeSection === "flashcards" ? null : "flashcards")}
        >
          <ol style={listStyle}>
            <li>Navigate to the <strong>Generator</strong> page from the sidebar</li>
            <li>Paste your notes or study material into the text box</li>
            <li>Click <strong>"🚀 Copy Prompt & Open AI"</strong> to generate flashcards via ChatGPT</li>
            <li>Copy the AI response and paste it back into the "Process Output" box</li>
            <li>Click <strong>"⚡ Generate Preview"</strong> to create your flashcard deck</li>
            <li>Save to Dashboard to access anytime for revision</li>
          </ol>
        </ToolSection>

        {/* RECALL SHEETS */}
        <ToolSection
          icon={<FaBrain size={24} color="#10b981" />}
          title="Active Recall Sheets"
          description="Create powerful self-testing sheets for active recall"
          isActive={activeSection === "recall"}
          onToggle={() => setActiveSection(activeSection === "recall" ? null : "recall")}
        >
          <ol style={listStyle}>
            <li>Go to <strong>Recall Sheets</strong> from the sidebar</li>
            <li>Select your subject and target length</li>
            <li>Paste your raw notes or article text</li>
            <li>Use the AI bridge: Click <strong>"🚀 Copy Prompt & Open AI"</strong></li>
            <li>Paste the prompt in ChatGPT, copy the response code block</li>
            <li>Return and paste into "Process Output"</li>
            <li>Generate and save your recall sheet to test yourself later</li>
          </ol>
        </ToolSection>

        {/* ATLAS TOOL */}
        <ToolSection
          icon={<FaGlobeAsia size={24} color="#3b82f6" />}
          title="Atlas Tool"
          description="Create interactive maps with pins, notes, and drawings"
          isActive={activeSection === "atlas"}
          onToggle={() => setActiveSection(activeSection === "atlas" ? null : "atlas")}
        >
          <ol style={listStyle}>
            <li>Open <strong>Atlas Tool</strong> from the sidebar</li>
            <li>Select a map region (India, World, Asia, etc.)</li>
            <li>Use the <strong>Pin Tool</strong> to mark locations with labels, tags, and notes</li>
            <li>Add custom symbols and colors to categorize your pins</li>
            <li>Use <strong>Draw/Shade</strong> tools to highlight regions or add annotations</li>
            <li>Click <strong>"Save to Dashboard"</strong> to preserve your work (login required)</li>
            <li>Access saved maps from the Dashboard's "Maps" tab for revision</li>
          </ol>
        </ToolSection>

        {/* DASHBOARD */}
        <ToolSection
          icon={<FaLayerGroup size={24} color="#e11d48" />}
          title="Dashboard & Revision"
          description="Track progress and revise all your saved materials"
          isActive={activeSection === "dashboard"}
          onToggle={() => setActiveSection(activeSection === "dashboard" ? null : "dashboard")}
        >
          <ol style={listStyle}>
            <li>Access the <strong>Dashboard</strong> from the sidebar (home page)</li>
            <li>View all your saved flashcards, recall sheets, and maps in one place</li>
            <li>Click on any item to start revision mode</li>
            <li>For Maps: Use <strong>"Identify & Score"</strong> mode to test your geography knowledge</li>
            <li>Track your scores and progress over time</li>
            <li>Delete or organize items using tags and filters</li>
          </ol>
        </ToolSection>
      </div>

      {/* DATA EXPORT/IMPORT SECTION */}
      <div style={{ ...stepCardStyle, border: "2px solid #16a34a", background: "#f0fdf4", position: "relative", overflow: "hidden" }}>
        <div style={{ ...iconContainerStyle, background: "#16a34a", color: "white" }}>💾</div>
        <div style={{ flex: 1 }}>
          <h3 style={stepTitleStyle}>Backup Your Data (Critical!)</h3>
          <p style={stepDescStyle}>
            Since all data is stored in your browser, it can be lost if you clear cache or switch devices.
          </p>
          <div style={{ marginTop: "15px", padding: "15px", background: "white", borderRadius: "8px", border: "1px solid #86efac" }}>
            <h4 style={{ margin: "0 0 10px 0", color: "#15803d", fontSize: "1.1rem" }}>How to Export:</h4>
            <ol style={{ ...listStyle, marginBottom: "15px" }}>
              <li>Click the <strong><FaDownload style={{ verticalAlign: "middle" }} /> "Export All Data"</strong> button in the sidebar</li>
              <li>A file will download containing all your flashcards, recall sheets, and maps</li>
              <li>Save this file in a safe location (Google Drive, OneDrive, etc.)</li>
            </ol>
            <h4 style={{ margin: "15px 0 10px 0", color: "#15803d", fontSize: "1.1rem" }}>How to Import:</h4>
            <ol style={listStyle}>
              <li>Click the <strong><FaUpload style={{ verticalAlign: "middle" }} /> "Load Data"</strong> button in the sidebar</li>
              <li>Select your previously exported .fgdata file</li>
              <li>All your progress will be restored instantly!</li>
            </ol>
          </div>

          {/* ANIMATED DEMO */}
          <div className="export-animation-demo" style={{
            marginTop: "20px",
            padding: "20px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "12px",
            position: "relative",
            height: "200px",
            overflow: "hidden"
          }}>
            <div className="virtual-sidebar" style={{
              position: "absolute",
              left: "20px",
              top: "20px",
              width: "100px",
              height: "160px",
              background: "white",
              borderRadius: "8px",
              padding: "10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
            }}>
              <div style={{ fontSize: "0.7rem", color: "#64748b", marginBottom: "8px" }}>Sidebar</div>
              <div className="export-btn-demo" style={{
                padding: "8px",
                background: "#f8fafc",
                border: "1px dashed #cbd5e1",
                borderRadius: "6px",
                fontSize: "0.65rem",
                textAlign: "center",
                color: "#475569",
                fontWeight: "600"
              }}>
                <FaDownload size={10} /> Export
              </div>
            </div>

            <div className="virtual-cursor" style={{
              position: "absolute",
              width: "20px",
              height: "20px",
              background: "#fbbf24",
              borderRadius: "50%",
              boxShadow: "0 0 20px rgba(251, 191, 36, 0.6)",
              animation: "cursorMove 3s ease-in-out infinite"
            }} />

            <div className="download-popup" style={{
              position: "absolute",
              right: "20px",
              bottom: "20px",
              background: "white",
              padding: "12px 16px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              fontSize: "0.8rem",
              fontWeight: "600",
              color: "#15803d",
              animation: "popupFade 3s ease-in-out infinite"
            }}>
              ✅ Data Exported!
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <button
          onClick={onNavigateToGenerator}
          className="generate-btn"
          style={{ padding: "16px 40px", fontSize: "1.2rem", background: "#e11d48" }}
        >
          Start Creating 🚀
        </button>
      </div>

      <style>{`
        @keyframes cursorMove {
          0%, 100% { left: 50%; top: 50%; opacity: 0; }
          20% { left: 50%; top: 50%; opacity: 1; }
          40% { left: 70px; top: 80px; opacity: 1; }
          50% { left: 70px; top: 80px; opacity: 1; transform: scale(1.3); }
          60% { left: 70px; top: 80px; opacity: 1; transform: scale(1); }
          80% { left: 50%; top: 50%; opacity: 0; }
        }

        @keyframes popupFade {
          0%, 40% { opacity: 0; transform: translateY(10px); }
          60%, 90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(10px); }
        }
      `}</style>
    </div>
  );
};

// Tool Section Component
const ToolSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  isActive: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ icon, title, description, isActive, onToggle, children }) => {
  return (
    <div
      style={{
        ...stepCardStyle,
        cursor: "pointer",
        border: isActive ? "2px solid #e11d48" : "1px solid #e2e8f0",
        background: isActive ? "#fff1f2" : "white",
        transition: "all 0.3s ease"
      }}
      onClick={onToggle}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "15px", width: "100%" }}>
        <div style={{ ...iconContainerStyle, background: isActive ? "#e11d48" : "#f1f5f9", color: isActive ? "white" : "#64748b" }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ ...stepTitleStyle, margin: "0 0 5px 0" }}>{title}</h3>
          <p style={{ ...stepDescStyle, margin: 0, fontSize: "0.9rem" }}>{description}</p>
        </div>
        <div style={{ fontSize: "1.5rem", color: "#94a3b8", transition: "transform 0.3s ease", transform: isActive ? "rotate(180deg)" : "rotate(0deg)" }}>
          ▼
        </div>
      </div>
      {isActive && (
        <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #fecdd3", width: "100%" }} onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      )}
    </div>
  );
};

// Styles
const stepCardStyle: React.CSSProperties = {
  background: "white",
  padding: "25px",
  borderRadius: "16px",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  display: "flex",
  flexWrap: "wrap",
  gap: "20px",
  alignItems: "flex-start",
};

const iconContainerStyle: React.CSSProperties = {
  minWidth: "50px",
  height: "50px",
  borderRadius: "12px",
  background: "#e2e8f0",
  color: "#64748b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.2rem",
  fontWeight: "bold",
  flexShrink: 0
};

const stepTitleStyle: React.CSSProperties = {
  margin: "0 0 10px 0",
  color: "#1e293b",
  fontSize: "1.3rem",
};

const stepDescStyle: React.CSSProperties = {
  margin: 0,
  color: "#64748b",
  lineHeight: "1.6",
  fontSize: "1.05rem",
};

const listStyle: React.CSSProperties = {
  paddingLeft: "20px",
  marginTop: "10px",
  lineHeight: "1.8",
  color: "#334155"
};